/**
 * Panel "Reestilizar 360" — selector visual liviano.
 *
 * No genera nada por sí mismo: arma una orden en lenguaje natural y la entrega
 * vía onConfirm. El que la manda al chat es ChatPanel (donde vive `send`). El
 * agente xvision-360 la interpreta (preset conocido o estilo nuevo sintetizado).
 */
import { useMemo, useState } from 'react';
import {
  getEstilos360,
  saveEstiloCustom,
  construirOrden,
  type Estilo360,
} from '../lib/estilos360';

interface Restyle360PanelProps {
  onConfirm: (orden: string) => void;
  onClose: () => void;
}

export function Restyle360Panel({ onConfirm, onClose }: Restyle360PanelProps) {
  const [estilos, setEstilos] = useState<Estilo360[]>(() => getEstilos360());
  const [sel, setSel] = useState<string | null>(null);
  const [animar, setAnimar] = useState(false);
  const [upscale, setUpscale] = useState(false);
  // Estilo nuevo
  const [creando, setCreando] = useState(false);
  const [nuevoLabel, setNuevoLabel] = useState('');
  const [nuevoDesc, setNuevoDesc] = useState('');
  const [guardar, setGuardar] = useState(true);

  const seleccionado = useMemo(() => estilos.find((e) => e.key === sel) ?? null, [estilos, sel]);

  const confirmarPreset = () => {
    if (!seleccionado) return;
    onConfirm(construirOrden({ estilo: seleccionado, animar, upscale }));
  };

  const confirmarNuevo = () => {
    const label = nuevoLabel.trim();
    const desc = nuevoDesc.trim();
    if (!label || !desc) return;
    const estilo: Estilo360 = guardar
      ? saveEstiloCustom(label, desc)
      : { key: 'tmp', label, desc, custom: true };
    if (guardar) setEstilos(getEstilos360());
    onConfirm(construirOrden({ estilo, animar, upscale }));
  };

  return (
    <div className="absolute inset-0 z-30 flex items-end md:items-center justify-center bg-black/60 p-3" onClick={onClose}>
      <div
        className="w-full max-w-2xl max-h-[80vh] overflow-y-auto bg-slate-900 border border-slate-700 rounded-2xl p-4 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-slate-200">🎨 Reestilizar esta 360</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-300 text-lg leading-none">×</button>
        </div>

        {!creando ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {estilos.map((e) => (
                <button
                  key={e.key}
                  onClick={() => setSel(e.key)}
                  className={`text-left p-2.5 rounded-lg border transition ${
                    sel === e.key
                      ? 'border-vision bg-vision-dark/30'
                      : 'border-slate-700 bg-slate-950 hover:border-slate-500'
                  }`}
                >
                  <div className="text-xs font-medium text-slate-200 flex items-center gap-1">
                    {e.label}
                    {e.custom && <span className="text-[10px] text-vision">★</span>}
                  </div>
                  <div className="text-[11px] text-slate-400 leading-tight mt-0.5">{e.desc}</div>
                </button>
              ))}
              <button
                onClick={() => setCreando(true)}
                className="p-2.5 rounded-lg border border-dashed border-slate-600 bg-slate-950 hover:border-vision text-slate-400 hover:text-vision text-xs font-medium flex items-center justify-center"
              >
                + Estilo nuevo
              </button>
            </div>

            <Toggles animar={animar} setAnimar={setAnimar} upscale={upscale} setUpscale={setUpscale} />

            <div className="flex justify-end gap-2 pt-1">
              <button onClick={onClose} className="px-3 py-1.5 text-xs text-slate-400 hover:text-slate-200">Cancelar</button>
              <button
                onClick={confirmarPreset}
                disabled={!seleccionado}
                className="px-4 py-1.5 text-xs rounded-lg bg-vision-dark hover:bg-vision text-white disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Reestilizar →
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="space-y-2">
              <label className="block text-xs text-slate-400">Nombre del estilo</label>
              <input
                value={nuevoLabel}
                onChange={(e) => setNuevoLabel(e.target.value)}
                placeholder="Ej: Acuarela japonesa nocturna"
                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:border-vision outline-none"
              />
              <label className="block text-xs text-slate-400 pt-1">Descripción (paleta, técnica, referencias)</label>
              <textarea
                value={nuevoDesc}
                onChange={(e) => setNuevoDesc(e.target.value)}
                placeholder="Ej: pincelada de tinta suelta, papel washi, azules índigo y blancos, faroles de papel encendidos, luna llena"
                rows={3}
                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:border-vision outline-none resize-none"
              />
              <label className="flex items-center gap-2 text-xs text-slate-400 pt-1">
                <input type="checkbox" checked={guardar} onChange={(e) => setGuardar(e.target.checked)} />
                Guardar este estilo para reusarlo
              </label>
            </div>

            <Toggles animar={animar} setAnimar={setAnimar} upscale={upscale} setUpscale={setUpscale} />

            <div className="flex justify-between gap-2 pt-1">
              <button onClick={() => setCreando(false)} className="px-3 py-1.5 text-xs text-slate-400 hover:text-slate-200">← Volver</button>
              <button
                onClick={confirmarNuevo}
                disabled={!nuevoLabel.trim() || !nuevoDesc.trim()}
                className="px-4 py-1.5 text-xs rounded-lg bg-vision-dark hover:bg-vision text-white disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Reestilizar →
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Toggles({
  animar,
  setAnimar,
  upscale,
  setUpscale,
}: {
  animar: boolean;
  setAnimar: (v: boolean) => void;
  upscale: boolean;
  setUpscale: (v: boolean) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2 border-t border-slate-800 pt-3">
      <button
        onClick={() => setAnimar(!animar)}
        className={`px-3 py-1.5 text-xs rounded-full border ${
          animar ? 'border-vision bg-vision-dark/40 text-white' : 'border-slate-700 text-slate-400'
        }`}
      >
        🎬 Animar a video {animar ? '✓' : ''}
      </button>
      <button
        onClick={() => setUpscale(!upscale)}
        className={`px-3 py-1.5 text-xs rounded-full border ${
          upscale ? 'border-vision bg-vision-dark/40 text-white' : 'border-slate-700 text-slate-400'
        }`}
      >
        ⬆ 4K {upscale ? '✓' : ''}
      </button>
    </div>
  );
}
